/**
 * Online Food Ordering App Store
 * Zustand store for application-wide state (theme, UI, etc.)
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { CookieConsent, Restaurant, ThemeMode } from '@/types';

interface AppState {
  // Theme
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;

  // Language
  language: 'en' | 'ar';
  setLanguage: (lang: 'en' | 'ar') => void;

  // Restaurant
  restaurant: Restaurant | null;
  setRestaurant: (restaurant: Restaurant | null) => void;

  // UI State
  cartDrawerOpen: boolean;
  setCartDrawerOpen: (open: boolean) => void;
  toggleCartDrawer: () => void;

  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;

  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;

  // Cookie Consent
  cookieConsent: CookieConsent | null;
  setCookieConsent: (consent: CookieConsent) => void;
  showCookieBanner: boolean;
  setShowCookieBanner: (show: boolean) => void;

  // Loading States
  globalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
}

// Augment ThemeMode type since it's defined in theme/index.ts
declare module '@/types' {
  export type ThemeMode = 'light' | 'dark';
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Theme
      themeMode: 'light' as ThemeMode,
      toggleTheme: () => {
        const newMode = get().themeMode === 'light' ? 'dark' : 'light';
        set({ themeMode: newMode });
        document.documentElement.setAttribute('data-theme', newMode);
      },
      setThemeMode: (mode) => {
        set({ themeMode: mode });
        document.documentElement.setAttribute('data-theme', mode);
      },

      // Language
      language: 'en' as 'en' | 'ar',
      setLanguage: (lang) => set({ language: lang }),

      // Restaurant
      restaurant: null,
      setRestaurant: (restaurant) => set({ restaurant }),

      // UI State
      cartDrawerOpen: false,
      setCartDrawerOpen: (open) => set({ cartDrawerOpen: open }),
      toggleCartDrawer: () => set((state) => ({ cartDrawerOpen: !state.cartDrawerOpen })),

      mobileMenuOpen: false,
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

      searchOpen: false,
      setSearchOpen: (open) => set({ searchOpen: open }),

      // Cookie Consent
      cookieConsent: null,
      setCookieConsent: (consent) => {
        set({ cookieConsent: consent, showCookieBanner: false });
      },
      showCookieBanner: true,
      setShowCookieBanner: (show) => set({ showCookieBanner: show }),

      // Loading
      globalLoading: false,
      setGlobalLoading: (loading) => set({ globalLoading: loading }),
    }),
    {
      name: 'food-ordering-app',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        themeMode: state.themeMode,
        language: state.language,
        cookieConsent: state.cookieConsent,
        showCookieBanner: state.showCookieBanner,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.themeMode) {
          document.documentElement.setAttribute('data-theme', state.themeMode);
        }
        // Show cookie banner if no consent stored
        if (!state?.cookieConsent) {
          state?.setShowCookieBanner?.(true);
        }
      },
    }
  )
);

// Selector hooks
export const useThemeMode = () => useAppStore((state) => state.themeMode);
export const useRestaurant = () => useAppStore((state) => state.restaurant);
export const useCartDrawerOpen = () => useAppStore((state) => state.cartDrawerOpen);
